/**
 * useSettings Hook - Provides reactive access to user settings
 * 
 * This hook loads settings from StorageService and provides
 * easy access to individual setting values.
 */

import { useState, useEffect, useCallback } from 'react';
import { storageService } from '../services/StorageService';

// Custom event name for settings updates
const SETTINGS_CHANGE_EVENT = 'settingsChanged';

/**
 * Dispatch a settings change event (call this when settings are updated)
 */
export function notifySettingsChange() {
  window.dispatchEvent(new CustomEvent(SETTINGS_CHANGE_EVENT));
}

/**
 * Hook to access and update user settings
 */
export function useSettings() {
  const [settings, setSettings] = useState(() => storageService.getSettings());

  // Listen for settings changes
  useEffect(() => {
    const handleSettingsChange = () => {
      setSettings(storageService.getSettings());
    };

    window.addEventListener(SETTINGS_CHANGE_EVENT, handleSettingsChange);
    // Also listen for storage events (cross-tab)
    window.addEventListener('storage', handleSettingsChange);

    return () => {
      window.removeEventListener(SETTINGS_CHANGE_EVENT, handleSettingsChange);
      window.removeEventListener('storage', handleSettingsChange);
    };
  }, []);

  // Refresh settings from storage
  const refreshSettings = useCallback(() => {
    setSettings(storageService.getSettings());
  }, []);

  // Update a single setting
  const updateSetting = useCallback((key, value) => {
    const newSettings = storageService.updateSetting(key, value);
    setSettings(newSettings);
    // Notify other components
    notifySettingsChange();
    return newSettings;
  }, []);

  // Get individual setting values with defaults
  const fontSize = settings.fontSize || 14;
  const showLineNumbers = settings.showLineNumbers !== false; // Default true
  const soundEnabled = settings.soundEnabled !== false; // Default true
  const hapticEnabled = settings.hapticEnabled !== false; // Default true
  const darkMode = settings.darkMode || false;
  const codeTheme = settings.codeTheme || 'monokai';

  return {
    settings,
    refreshSettings,
    updateSetting,
    // Individual settings with defaults
    fontSize,
    showLineNumbers,
    soundEnabled,
    hapticEnabled,
    darkMode,
    codeTheme
  };
}

/**
 * Hook to get just the code editor settings - REACTIVE
 * Re-renders when settings change
 */
export function useCodeEditorSettings() {
  const [settings, setSettings] = useState(() => storageService.getSettings());

  // Listen for settings changes - this makes it reactive!
  useEffect(() => {
    const handleSettingsChange = () => {
      const newSettings = storageService.getSettings();
      setSettings(newSettings);
      
      // Also apply CSS variable when settings change
      const fontSize = newSettings.fontSize || 14;
      document.documentElement.style.setProperty('--code-font-size', `${fontSize}px`);
    };

    window.addEventListener(SETTINGS_CHANGE_EVENT, handleSettingsChange);
    window.addEventListener('storage', handleSettingsChange);

    // Apply initial CSS variable
    const fontSize = settings.fontSize || 14;
    document.documentElement.style.setProperty('--code-font-size', `${fontSize}px`);

    return () => {
      window.removeEventListener(SETTINGS_CHANGE_EVENT, handleSettingsChange);
      window.removeEventListener('storage', handleSettingsChange);
    };
  }, []);

  return {
    fontSize: settings.fontSize || 14,
    showLineNumbers: settings.showLineNumbers !== false,
    codeTheme: settings.codeTheme || 'monokai'
  };
}

export default useSettings;